import { Injectable } from '@angular/core';
import { Functions } from '@angular/fire/functions';
import { AppStoreDispatcher } from '../state/app.state.dispatcher';
import { addDoc, collection, doc, Firestore, getDoc, setDoc, updateDoc, DocumentReference, getDocs } from '@angular/fire/firestore';
import { ProfileStateDispatcher } from '../state/profile/profile.dispatcher';

@Injectable({
    providedIn: 'root'
})
export class ConversationsService {

    constructor(public cloudFNs: Functions,
        public appStateDispatch: AppStoreDispatcher,
        public firestore: Firestore,
        public profileDispatch: ProfileStateDispatcher,
    ) {}

    /**
     *  Load conversations list
     */
    public async loadConversations(): Promise < any > {
        const myID = await this.profileDispatch.getMyID();
        return getDocs(this.getUserConversationsQuery(myID)).then(querySnapshot => {
            const promises: any[] = [];
            querySnapshot.forEach(doc => {
                const conversationID = doc.id; // or doc.data().conversationID
                promises.push(
                    Promise.all([
                        // Get conversation details
                        getDoc(this.getConversationID(conversationID)).then(conversationDoc => {
                            return conversationDoc.data();
                        }),
                        // Get members details
                        getDocs(this.getConversationMembersQuery(conversationID)).then(snapshot => {
                            const promises2: any[] = [];
                            snapshot.forEach(memberDoc => {
                                const userID = memberDoc.id; // or doc.data().userID
                                promises2.push(new Promise(resolve => resolve(memberDoc.data().unreadCount)));
                                promises2.push(
                                    getDoc(this.getUsersID(userID)).then(userDoc => {
                                        return { ...userDoc.data() };
                                    })
                                );
                            });
                            return Promise.all(promises2).then(values => {
                                const members: any[] = [];
                                for (let _i = 0; _i < values.length / 2; _i++) {
                                    members.push({ unreadCount: values[_i * 2], ...values[_i * 2 + 1] });
                                }
                                return members;
                            });
                        })
                    ]).then(values => {
                        if (values[0] === undefined) {
                            throw new Error(`Values[0] is undefined in conversations service`);
                        }
                        return {
                            id: conversationID,
                            details: values[0].details,
                            members: values[1],
                            myID: myID
                        };
                    })
                );
            });
            return Promise.all(promises).then(conversations => {
                return conversations;
            });
        });
    }

    getUserConversationsQuery(ID: string) {
        return collection(this.firestore, `users/${ID}/conversations`);
    }

    getUserConversationsDoc(ID: string, docRefId: string) {
        return doc(this.firestore, `users/${ID}/conversations/${docRefId}`);
    }

    getConversationMembersDoc(docRefID: string, ID: string) {
        return doc(this.firestore, `conversations/${docRefID}/members/${ID}`)
    }

    getConversationMembersQuery(ID: string) {
        return collection(this.firestore, `conversations/${ID}/members`); 
    }

    getConversationID(ID: string) {
        return doc(this.firestore, `conversations/${ID}`) as DocumentReference<any>;
    }

    getUserContacts(myID: string, userID: string) {
        return doc(this.firestore, `users/${myID}/contacts/${userID}`);
    }

    getUserConversations(myID, docRefID) {
        return doc(this.firestore, `users/${myID}/conversations/${docRefID}`);
    }

    getUsersID(userID: string) {
        return doc(this.firestore, `users/${userID}`) as DocumentReference<any>;
    }

    /**
     * Create new conversation with user
     * @param userID The user's uid
     */
    public async createNewConversation(userID: string): Promise < any > {
        const myID = await this.profileDispatch.getMyID();
        const collReference = collection(this.firestore, `conversations`);
        return addDoc((collReference), {
            details: {
                lastTimestamp: null,
                lastMessage: null,
                lastSenderID: null
            }
        })
            .then(docRef => {
                console.log('Document written with ID: ', docRef.id);
                return Promise.all([
                    updateDoc(this.getConversationID(docRef.id), {
                        id: docRef.id
                    }),
                    // Add members to the new conversation
                    setDoc(this.getConversationMembersDoc(docRef.id, myID), {
                        userID: myID,
                        lastReadTimestamp: null
                    }),
                    setDoc(this.getConversationMembersDoc(docRef.id, userID), {
                        userID: userID,
                        lastReadTimestamp: null
                    }),
                    // Add conversation id to user's conversations collection and my conversations collection
                    setDoc(this.getUserConversations(myID, docRef.id), {
                        conversationID: docRef.id
                    }),
                    setDoc(this.getUserConversationsDoc(userID, docRef.id), {
                        conversationID: docRef.id
                    }),
                    // Add conversation id to user's contacts collection and my contacts collection
                    updateDoc(this.getUserContacts(myID, userID), {
                        conversationID: docRef.id
                    }),
                    updateDoc(this.getUserContacts(userID, myID), {
                        conversationID: docRef.id
                    })
                ]);
            })
            .catch(error => {
                console.error('Error adding document: ', error);
                return {
                    success: false,
                    error: error
                };
            });
    }

}
